import {
  BILLING_PLAN_ORDER,
  BILLING_PLANS,
  type BillingPlanKey,
  isSunsetPlanKey,
} from './plans'

/**
 * Whether a subscription status permits an in-app plan reprice
 * (upgrade / downgrade / keep). `changeOrgSubscriptionPlan` only runs for
 * `active`/`trialing`; anything else (past_due, unpaid, incomplete, …) must
 * fix payment in the Stripe portal first, so those CTAs route to Manage
 * Billing instead of opening a confirm dialog that then 409s. `undefined`
 * (status unknown) is treated as healthy so callers that omit it keep their
 * prior behavior.
 */
export function isInAppRepriceableStatus(status: string | undefined): boolean {
  return status === undefined || status === 'active' || status === 'trialing'
}

/** Stripe supports both a period-end flag and an explicit future cancel date. */
export function hasScheduledCancellation(args: {
  cancelAtPeriodEnd?: boolean
  cancelAt?: number | null
  nowMs: number
}): boolean {
  return (
    args.cancelAtPeriodEnd === true ||
    (typeof args.cancelAt === 'number' && args.cancelAt > args.nowMs)
  )
}

/**
 * Is this subscription inside a PREPAID TERM (the 6-month Growth commitment)?
 *
 * The term is modelled as a Stripe trial: the customer paid the whole term up
 * front as a one-time invoice item, and the recurring price does not start
 * charging until `trial_end`. Brew's own billing creates a trial in exactly one
 * place — `createOrgCheckoutSession`'s commitment branch — and stamps an exact
 * metadata marker that is persisted on `billingAccounts`. Status alone is not
 * proof: Stripe also uses `trialing` for ordinary and operational trials.
 *
 * Why it matters: Stripe CANNOT prorate a trial. Repricing the item mid-term
 * therefore produces a $0 invoice and hands the customer the more expensive tier
 * for the remainder of a term they paid the cheaper rate for — verified against
 * Stripe, not assumed. Plan changes are blocked until the term ends;
 * cancellation is NOT blocked, because that just stops the renewal and the org
 * rightly keeps what it already paid for.
 *
 * The block is TIME-BOUNDED by the account's `currentPeriodEnd`: the promo
 * metadata marker is permanent on the Stripe subscription, so without the
 * bound, any post-term return to `trialing` (a support-granted trial, a
 * pause/resume) would lock the org out of plan changes forever. An unknown
 * period end keeps the protective block — the sync path writes
 * `currentPeriodEnd` on every subscription event, so "unknown" cannot persist,
 * and blocking a reprice during ANY trial is the safe direction (Stripe cannot
 * prorate trials at all).
 */
export function isInPrepaidTerm(args: {
  status?: string
  isGrowthCommitment?: boolean
  currentPeriodEnd: number | null | undefined
  nowMs: number
}): boolean {
  if (args.status !== 'trialing' || args.isGrowthCommitment !== true) {
    return false
  }
  if (typeof args.currentPeriodEnd === 'number') {
    return args.nowMs <= args.currentPeriodEnd
  }
  return true
}

/**
 * States in which the upgrade dialog must hand off to /plan instead of
 * repricing in place — `changeOrgSubscriptionPlan` deterministically rejects
 * each of them, and /plan already renders the correct affordance: "Keep" for a
 * scheduled downgrade, Manage Billing for a payment issue, and the disabled
 * prepaid-term CTAs with their explanatory hover.
 */
export function shouldDeferPlanChangeToPlanPage(args: {
  portalEligible: boolean
  hasScheduledPlanChange: boolean
  status: string | undefined
  isGrowthCommitment?: boolean
  currentPeriodEnd: number | null | undefined
  nowMs: number
}): boolean {
  return (
    args.portalEligible &&
    (args.hasScheduledPlanChange ||
      !isInAppRepriceableStatus(args.status) ||
      isInPrepaidTerm(args))
  )
}

/**
 * Why a plan change is refused during a prepaid term. Names the date the term
 * ends and the one action that IS available, so the message is a next step
 * rather than a dead end.
 */
export function buildPrepaidTermChangeBlockedMessage(
  currentPeriodEnd: number | null | undefined
): string {
  const termEnd =
    typeof currentPeriodEnd === 'number'
      ? new Date(currentPeriodEnd).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : 'the end of your prepaid term'

  return `Your plan is prepaid through ${termEnd}. You can change plans once the term ends — or cancel now to stop it renewing.`
}

export type BillingPlanActionVariant =
  | 'default'
  | 'upgrade'
  | 'downgrade'
  | 'current'

/**
 * What clicking a plan card's button should DO. The grid is a thin dispatcher
 * that switches on this — keeping the policy (which action for which
 * current→target + subscription state) pure and unit-testable here.
 *
 *  - `checkout`   start Stripe Checkout for a NEW subscription (free org)
 *  - `upgrade`    reprice the existing subscription up (immediate, prorated)
 *  - `downgrade`  reprice down (takes effect at period end)
 *  - `cancel`     cancel the subscription at period end (paid → Free)
 *  - `resume`     undo a pending cancellation
 *  - `keep`       undo a scheduled DOWNGRADE — restore the current tier's price
 *  - `manage`     open the Stripe customer portal (payment method / invoices)
 *  - `contact`    open the Enterprise sales link
 *  - `signup`     open the sign-up flow (signed-out)
 *  - `select-org` route to org creation (signed-in, no org)
 *  - `none`       disabled / no-op
 */
export type BillingPlanActionIntent =
  | 'checkout'
  | 'upgrade'
  | 'downgrade'
  | 'cancel'
  | 'resume'
  | 'keep'
  | 'manage'
  | 'contact'
  | 'signup'
  | 'select-org'
  | 'none'

export type BillingCycleDateState = {
  /**
   * `Billing starts` is the prepaid-term case: the org already paid for the
   * whole term, so nothing "renews" on that date — recurring charges BEGIN.
   */
  label: 'Renews on' | 'Access until' | 'Ended on' | 'Billing starts'
  date: number | null
}

export type BillingPlanActionState = {
  buttonText: string
  buttonVariant: BillingPlanActionVariant
  disabled: boolean
  intent: BillingPlanActionIntent
  currentButtonHoverText?: string
}

export function getBillingCycleDateState(args: {
  status: string
  cancelAtPeriodEnd: boolean
  cancelAt: number | null | undefined
  currentPeriodEnd: number | null | undefined
  nowMs: number
  isGrowthCommitment: boolean | undefined
}): BillingCycleDateState {
  const hasFutureCancelAt =
    typeof args.cancelAt === 'number' && args.cancelAt > args.nowMs
  const hasFuturePeriodEnd =
    typeof args.currentPeriodEnd === 'number' &&
    args.currentPeriodEnd > args.nowMs

  if (hasFutureCancelAt) {
    return {
      label: 'Access until',
      date: args.cancelAt ?? null,
    }
  }

  if (args.cancelAtPeriodEnd && hasFuturePeriodEnd) {
    return {
      label: 'Access until',
      date: args.currentPeriodEnd ?? null,
    }
  }

  if (args.status === 'canceled') {
    return {
      label: 'Ended on',
      date: args.cancelAt ?? args.currentPeriodEnd ?? null,
    }
  }

  // A prepaid term is already paid in full, so the term end is when monthly
  // billing BEGINS — calling it "Renews on" implies a charge that already
  // happened and hides the one that is coming.
  if (isInPrepaidTerm(args)) {
    return {
      label: 'Billing starts',
      date: args.currentPeriodEnd ?? null,
    }
  }

  return {
    label: 'Renews on',
    date: args.currentPeriodEnd ?? null,
  }
}

export function getBillingPlanActionState(args: {
  planKey: BillingPlanKey
  currentPlanKey: BillingPlanKey
  isSignedIn: boolean
  hasOrganization: boolean
  portalEligible: boolean
  /** True when the org's subscription is set to cancel at period end. */
  cancelAtPeriodEnd?: boolean
  cancelAt?: number | null
  /** True when a downgrade is scheduled for the next renewal. */
  hasScheduledDowngrade?: boolean
  /** The plan the scheduled downgrade lands on (the next-renewal tier). */
  scheduledPlanKey?: BillingPlanKey | null
  /**
   * Stripe subscription status. When it isn't `active`/`trialing` the in-app
   * reprice action rejects the change, so upgrade/downgrade/keep CTAs route to
   * Manage Billing instead. Omit to keep prior (reprice-allowed) behavior.
   */
  status?: string
  isGrowthCommitment?: boolean
  /**
   * Account/overview `currentPeriodEnd` (ms) + the caller's clock. Both are
   * required so every surface time-bounds the prepaid-term block the same way
   * (see `isInPrepaidTerm`); an undefined period end keeps the protective
   * block.
   */
  currentPeriodEnd: number | null | undefined
  nowMs: number
}): BillingPlanActionState {
  const plan = BILLING_PLANS[args.planKey]
  const isCurrentPlan = args.planKey === args.currentPlanKey
  const currentPlanIndex = BILLING_PLAN_ORDER.indexOf(args.currentPlanKey)
  const planIndex = BILLING_PLAN_ORDER.indexOf(args.planKey)
  const isUpgrade =
    !isCurrentPlan && currentPlanIndex !== -1 && planIndex > currentPlanIndex
  const isDowngrade =
    !isCurrentPlan && currentPlanIndex !== -1 && planIndex < currentPlanIndex
  // A healthy subscription can be repriced in-app; otherwise the action 409s
  // and these surfaces send the user to the portal to fix payment first.
  const isHealthy = isInAppRepriceableStatus(args.status)
  const cancellationPending = hasScheduledCancellation(args)

  // Inside a prepaid term the subscription cannot be repriced (see
  // `isInPrepaidTerm`), so no OTHER paid plan's card may offer an action the
  // server will reject. The org's own card falls through to the
  // `isCurrentPlan` branch below (Manage Billing), and the FREE card falls
  // through to the cancel branch — cancellation is explicitly NOT blocked
  // during the term (it only stops the renewal; the org keeps what it paid
  // for), and the Free card is the only in-app cancel affordance.
  if (!isCurrentPlan && args.planKey !== 'free' && isInPrepaidTerm(args)) {
    return {
      buttonText: isUpgrade ? 'Upgrade' : 'Downgrade',
      buttonVariant: isUpgrade ? 'upgrade' : 'downgrade',
      disabled: true,
      intent: 'none',
      currentButtonHoverText: `Available when your prepaid ${BILLING_PLANS[args.currentPlanKey].name} term ends`,
    }
  }

  // A sunset tier is grandfathered, not purchasable. Its card renders only for
  // the viewer already on it (the `isCurrentPlan` branch below owns that), so
  // every other path to it — including a paid org DOWNGRADING into it — closes
  // here. `createOrgCheckoutSession` and `changeOrgSubscriptionPlan` enforce
  // the same rule server-side; this is the UI half.
  //
  // Exception: a downgrade ALREADY scheduled onto this tier (booked before the
  // sunset) is a real pending state. That card must keep reading "Scheduled"
  // further down — telling the user a plan they are actively moving to is "no
  // longer available" would be both alarming and wrong.
  const isScheduledTarget =
    (args.hasScheduledDowngrade ?? false) &&
    args.planKey === args.scheduledPlanKey

  if (!isCurrentPlan && !isScheduledTarget && isSunsetPlanKey(args.planKey)) {
    return {
      buttonText: 'No longer available',
      buttonVariant: 'default',
      disabled: true,
      intent: 'none',
    }
  }

  if (!args.isSignedIn && args.planKey !== 'enterprise') {
    return {
      buttonText:
        args.planKey === 'free' ? 'Start Free' : `Start with ${plan.name}`,
      buttonVariant: 'default',
      disabled: false,
      intent: 'signup',
    }
  }

  if (
    args.isSignedIn &&
    !args.hasOrganization &&
    args.planKey !== 'enterprise'
  ) {
    return {
      buttonText: 'Select Organization',
      buttonVariant: 'default',
      disabled: false,
      intent: 'select-org',
    }
  }

  if (isCurrentPlan) {
    if (args.planKey !== 'free' && args.portalEligible) {
      // A pending cancellation turns the current-plan button into a one-click
      // "Resume" affordance so the user can undo it without the portal.
      if (cancellationPending) {
        return {
          buttonText: 'Resume plan',
          buttonVariant: 'upgrade',
          disabled: false,
          intent: 'resume',
        }
      }

      // A pending DOWNGRADE shows the still-entitled tier as current; offer a
      // one-click "Keep" to cancel the scheduled change (restores this tier's
      // price, no charge) — otherwise there's no in-app way to undo it. "Keep"
      // reprices, so only offer it while healthy; past_due falls to Manage
      // Billing below (fix payment in the portal first).
      if (args.hasScheduledDowngrade && isHealthy) {
        return {
          buttonText: `Keep ${plan.name}`,
          buttonVariant: 'upgrade',
          disabled: false,
          intent: 'keep',
        }
      }

      return {
        buttonText: 'Current',
        buttonVariant: 'current',
        disabled: false,
        intent: 'manage',
        currentButtonHoverText: 'Manage plan',
      }
    }

    return {
      buttonText: 'Current',
      buttonVariant: 'current',
      disabled: true,
      intent: 'none',
    }
  }

  if (args.planKey === 'enterprise') {
    return {
      buttonText: 'Talk to Sales',
      buttonVariant: 'default',
      disabled: false,
      intent: 'contact',
    }
  }

  if (args.portalEligible) {
    // Payment problem (past_due / unpaid / incomplete): the in-app reprice
    // action rejects the change, so route paid-tier upgrades/downgrades to
    // Manage Billing (fix the card in the portal) rather than opening a confirm
    // that 409s. Free stays a cancellation (allowed while past_due); the
    // current card is handled above.
    if (!isHealthy && args.planKey !== 'free' && (isUpgrade || isDowngrade)) {
      return {
        buttonText: isUpgrade ? 'Upgrade' : 'Downgrade',
        buttonVariant: isUpgrade ? 'upgrade' : 'downgrade',
        disabled: false,
        intent: 'manage',
        currentButtonHoverText: 'Resolve billing first',
      }
    }

    // A pending cancellation can only be undone via "Resume" on the current
    // card. Switching tiers here would reprice and re-commit the org to paid
    // billing it had set to end, so disable those CTAs (mirrors the
    // scheduled-downgrade "Keep first" rule). The Free card is the scheduled
    // destination, so label it "Scheduled".
    if (cancellationPending) {
      if (args.planKey === 'free') {
        return {
          buttonText: 'Scheduled',
          buttonVariant: 'downgrade',
          disabled: true,
          intent: 'none',
        }
      }
      return {
        buttonText: isUpgrade ? 'Upgrade' : 'Downgrade',
        buttonVariant: isUpgrade ? 'upgrade' : 'downgrade',
        disabled: true,
        intent: 'none',
        currentButtonHoverText: `Resume ${BILLING_PLANS[args.currentPlanKey].name} first`,
      }
    }

    // The tier a scheduled downgrade lands on — show it as already scheduled
    // (not a fresh "Downgrade") so re-confirming it is a clear no-op.
    if (args.hasScheduledDowngrade && args.planKey === args.scheduledPlanKey) {
      return {
        buttonText: 'Scheduled',
        buttonVariant: 'downgrade',
        disabled: true,
        intent: 'none',
      }
    }

    // Paid org choosing a different tier — change the existing subscription
    // in place. Downgrading to Free is a cancellation, not a reprice.
    if (args.planKey === 'free') {
      return {
        buttonText: 'Cancel plan',
        buttonVariant: 'downgrade',
        disabled: false,
        intent: 'cancel',
      }
    }

    // While a downgrade is scheduled, the Stripe price sits below the entitled
    // tier, so an in-app upgrade can't be priced correctly (the action rejects
    // it). Disable the CTA with a hint to cancel the scheduled change first,
    // rather than offer an "Upgrade" that always fails. (Re-targeting the
    // downgrade to a different lower tier still works, so downgrades stay live.)
    if (isUpgrade && args.hasScheduledDowngrade) {
      return {
        buttonText: 'Upgrade',
        buttonVariant: 'upgrade',
        disabled: true,
        intent: 'none',
        currentButtonHoverText: `Keep ${BILLING_PLANS[args.currentPlanKey].name} first, then upgrade`,
      }
    }

    if (isUpgrade) {
      return {
        buttonText: 'Upgrade',
        buttonVariant: 'upgrade',
        disabled: false,
        intent: 'upgrade',
      }
    }

    if (isDowngrade) {
      return {
        buttonText: 'Downgrade',
        buttonVariant: 'downgrade',
        disabled: false,
        intent: 'downgrade',
      }
    }

    return {
      buttonText: 'Manage plan',
      buttonVariant: 'default',
      disabled: false,
      intent: 'manage',
    }
  }

  // Free / unsubscribed org — any paid plan starts a NEW checkout.
  if (isUpgrade) {
    return {
      buttonText: 'Upgrade',
      buttonVariant: 'upgrade',
      disabled: false,
      intent: 'checkout',
    }
  }

  if (isDowngrade) {
    return {
      buttonText: 'Downgrade',
      buttonVariant: 'downgrade',
      disabled: false,
      intent: 'checkout',
    }
  }

  return {
    buttonText: plan.buttonText,
    buttonVariant: 'default',
    disabled: false,
    intent: 'checkout',
  }
}
